import type { DigitalAssetView } from '@yingbaba/types';
import { FileAttachment } from '@yingbaba/ui';
import { datetime, money } from '@yingbaba/utils';

export const assetCategories: Record<string, string> = {
  video: '视频',
  image: '图片',
  audio: '音频',
  template: '后期模板',
  model3d: '三维素材',
};
const licenseNames: Record<string, string> = {
  personal: '个人许可',
  business: '商业许可',
  extended: '扩展许可',
};
const clearance = (value: string) =>
  value === 'none' ? '不涉及' : value === 'cleared' ? '已声明取得授权' : '待核对';

export function DigitalAssetMaterials({ value: v }: { value: DigitalAssetView }) {
  const facts = [
    ['权利人', v.rights.holder_name || '未填写'],
    [
      '权利来源',
      v.rights.basis === 'original'
        ? '原创'
        : v.rights.basis === 'authorized'
          ? '已获再许可授权'
          : '未填写',
    ],
    [
      '授权地区',
      v.rights.territory === 'CN'
        ? '中国大陆'
        : v.rights.territory === 'worldwide'
          ? '全球'
          : '未填写',
    ],
    ['销售截止', v.rights.sale_ends_at ? datetime(v.rights.sale_ends_at) : '未限定'],
    ['第三方内容', clearance(v.rights.third_party_clearance)],
    ['肖像与声音', clearance(v.rights.likeness_clearance)],
    ['音乐', clearance(v.rights.music_clearance)],
    ['允许预览', v.rights.can_preview ? '已声明' : '未声明'],
    ['允许再许可', v.rights.can_sublicense ? '已声明' : '未声明'],
    ['扩展授权权利', v.rights.extended_rights ? '已声明持有' : '未声明'],
    ['已售许可存续', v.rights.existing_licenses_survive ? '已确认' : '未确认'],
  ];
  return (
    <div className="digital-review-materials">
      <section>
        <h3>{v.release.title}</h3>
        <p className="small muted">
          {assetCategories[v.release.category] || v.release.category} · 版本 {v.release.version}
        </p>
        <p className="prose">{v.release.description}</p>
        <dl className="digital-review-facts">
          <div>
            <dt>技术规格</dt>
            <dd>{v.release.specifications || '未填写'}</dd>
          </div>
          <div>
            <dt>软件与依赖</dt>
            <dd>{v.release.software || '未填写'}</dd>
          </div>
        </dl>
        {v.release.review_reason && (
          <p className="small">上次审核意见：{v.release.review_reason}</p>
        )}
      </section>
      <section>
        <h3>权利声明与定价</h3>
        <dl className="digital-review-facts">
          {facts.map(([name, value]) => (
            <div key={name}>
              <dt>{name}</dt>
              <dd>{value}</dd>
            </div>
          ))}
          <div>
            <dt>AI 创作</dt>
            <dd>
              {v.rights.ai_generated
                ? v.rights.ai_provenance || '已声明包含 AI，未补充来源'
                : '声明未包含 AI'}
            </dd>
          </div>
        </dl>
        <div className="row wrap">
          {v.prices.map((p) => (
            <span className="tag" key={p.code}>
              {licenseNames[p.code] || p.code} {money(p.amount_minor)}
            </span>
          ))}
        </div>
      </section>
      {[
        ['preview', '展示预览', '核对展示效果及预览保护。'],
        ['original', '原始文件', '核对可交付内容、格式和技术质量。'],
        ['rights_proof', '权属证据', '核对权利链、第三方许可与再许可范围。'],
      ].map(([kind, title, hint]) => (
        <section key={kind} aria-label={title}>
          <h3>{title}</h3>
          <p className="small muted">{hint}</p>
          {!v.files.some((f) => f.kind === kind) && <p className="small muted">尚未提供{title}</p>}
          {v.files
            .filter((f) => f.kind === kind)
            .map((f) => {
              const path = `/admin/digital/assets/${v.asset.id}/files/${f.id}`;
              return (
                <div className="digital-review-file" key={f.id}>
                  <FileAttachment
                    name={f.name}
                    mime={f.mime}
                    size={f.size}
                    previewPath={`${path}?preview=1`}
                    downloadPath={path}
                    withOrganization={false}
                  />
                  <details className="small muted">
                    <summary>文件校验信息</summary>
                    <p>SHA256：{f.sha256}</p>
                  </details>
                </div>
              );
            })}
        </section>
      ))}
    </div>
  );
}
